/**
 * Scroll reveal engine.
 *
 * Contract:
 *   <div data-reveal>            reveals as one unit
 *   <div data-reveal="stagger">  each direct child reveals individually
 *
 * Stagger offsets come from the observer callback batch, not a render-time index, so an
 * element scrolling into view alone fires immediately instead of inheriting a large delay
 * from its position in the grid.
 */

const REVEAL_CLASS = 'reveal--in';
const STAGGER_CAP = 8;

const UNIT_SELECTOR = '[data-reveal]:not([data-reveal="stagger"])';
const STAGGER_CHILD_SELECTOR = '[data-reveal="stagger"] > *';

/**
 * @param {Element[]} elements
 */
function reveal(elements) {
  // IntersectionObserver does not guarantee entry order matches DOM order
  const ordered = elements.sort((a, b) =>
    a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
  );

  ordered.forEach((element, index) => {
    element.style.setProperty('--reveal-i', String(Math.min(index, STAGGER_CAP)));
    element.classList.add(REVEAL_CLASS);
  });
}

const observer = new IntersectionObserver(
  (entries) => {
    const visible = [];

    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      observer.unobserve(entry.target);
      visible.push(entry.target);
    }

    if (visible.length > 0) reveal(visible);
  },
  { threshold: 0, rootMargin: '0px 0px -10% 0px' }
);

/**
 * @param {Element} element
 * @returns {boolean}
 */
function isNestedUnit(element) {
  return element.matches(UNIT_SELECTOR) && Boolean(element.parentElement?.closest(UNIT_SELECTOR));
}

/**
 * @param {ParentNode} root
 */
function observe(root) {
  /** @type {Set<Element>} */
  const targets = new Set();

  if (root instanceof Element) {
    if (root.matches(UNIT_SELECTOR)) targets.add(root);
    if (root.matches('[data-reveal="stagger"]')) {
      for (const child of root.children) targets.add(child);
    }
  }

  for (const element of root.querySelectorAll(UNIT_SELECTOR)) targets.add(element);
  for (const element of root.querySelectorAll(STAGGER_CHILD_SELECTOR)) targets.add(element);

  for (const target of targets) {
    if (target.classList.contains(REVEAL_CLASS)) continue;
    if (isNestedUnit(target)) continue;
    observer.observe(target);
  }
}

observe(document);

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) observe(event.target);
  });

  // A merchant must never select a block that is sitting at opacity 0
  document.addEventListener('shopify:block:select', (event) => {
    let node = event.target instanceof Element ? event.target : null;

    while (node) {
      if (node.matches(UNIT_SELECTOR) || node.parentElement?.matches('[data-reveal="stagger"]')) {
        observer.unobserve(node);
        node.classList.add(REVEAL_CLASS);
      }
      node = node.parentElement;
    }
  });
}
